/**
 * Où envoyer un lien Supabase qui a atterri sur la mauvelle page.
 *
 * Les liens des e-mails Supabase (confirmation d'inscription, reset de mot de
 * passe, invitation, magic link) redirigent vers l'URL `redirectTo` demandée
 * SEULEMENT si elle est dans la liste blanche du projet Supabase. Sinon Supabase
 * retombe sur la Site URL, c'est-à-dire la racine `kaiylo.fr/`, avec la session
 * (ou l'erreur) dans le fragment `#…`. Notre client supabase-js est en mode
 * PKCE et refuse ce fragment « implicit » (« Not a valid PKCE flow url ») : la
 * personne se retrouvait sur la landing, pas connectée, sans aucun message
 * (cas Hind, 25/09/2026 : elle a confirmé son e-mail puis a cru que ça n'avait
 * rien fait).
 *
 * Cette fonction est pure : elle reçoit le chemin courant et le fragment, et
 * rend le chemin de la page qui sait traiter ce fragment, ou null s'il n'y a
 * rien à faire. Le fragment est conservé tel quel par l'appelant.
 *
 * @param {string} pathname  ex. '/'
 * @param {string} hash      ex. '#access_token=…&refresh_token=…&type=signup'
 * @returns {string|null}    ex. '/auth/confirm-email'
 */
export const AUTH_CALLBACK_PATHS = ['/auth/confirm-email', '/auth/callback', '/reset-password'];

export function getAuthHashRedirect(pathname, hash) {
  if (!hash || hash.length < 2) return null;
  // Seule la racine reçoit ces liens (repli de Supabase sur la Site URL). Le
  // fragment reste en mémoire pendant toute la visite : le rattraper sur une
  // autre page renvoyait sur /auth/confirm-email dès qu'on cliquait « Aller à
  // la page de connexion » depuis un lien expiré, et le bouton semblait mort.
  if (pathname !== '/') return null;
  if (AUTH_CALLBACK_PATHS.includes(pathname)) return null;

  const params = new URLSearchParams(hash.replace(/^#/, ''));
  const hasSession = params.has('access_token') && params.has('refresh_token');
  const hasError = params.has('error') || params.has('error_description') || params.has('error_code');
  if (!hasSession && !hasError) return null;

  const type = params.get('type');
  if (type === 'recovery') return '/reset-password';
  // signup, invite, magiclink, email_change… et les liens en erreur (lien
  // expiré ou déjà utilisé) : la page de confirmation sait afficher les deux.
  return '/auth/confirm-email';
}

/**
 * Extrait la paire de jetons d'un fragment « implicit » Supabase.
 * @returns {{ access_token: string, refresh_token: string, type: string|null }|null}
 */
export function parseImplicitSession(hash) {
  if (!hash) return null;
  const params = new URLSearchParams(hash.replace(/^#/, ''));
  const access_token = params.get('access_token');
  const refresh_token = params.get('refresh_token');
  if (!access_token || !refresh_token) return null;
  return { access_token, refresh_token, type: params.get('type') };
}

/**
 * Cache le fragment de callback Supabase à supabase-js.
 *
 * Le client lit `window.location.href` pendant son initialisation (différée :
 * elle attend un verrou `navigator.locks`, donc PAS dans le constructeur). En
 * mode PKCE, un fragment `#access_token=…` le fait échouer (« Not a valid PKCE
 * flow url ») ET il appelle `_removeSession()` ; même chose pour un fragment
 * `#error=…` (lien expiré). La session locale éventuelle est effacée et un
 * SIGNED_OUT est émis, que l'AuthContext traduit en redirection vers /login :
 * la personne qui vient de cliquer un lien de confirmation atterrit sur le
 * formulaire de connexion sans explication.
 *
 * `stashImplicitAuthHash()` est appelé avant `createClient()` : le fragment
 * est retiré de l'URL et gardé EN MÉMOIRE (jamais remis dans l'URL, sinon le
 * client différé le retrouverait). Les pages de callback le lisent via
 * `readAuthHash()` et installent la session elles-mêmes avec `setSession()`.
 * Un rechargement de la page perd le fragment : sans conséquence, ces jetons
 * sont à usage unique.
 */
let stashedHash = null;

export function isSupabaseCallbackHash(hash) {
  if (!hash || hash.length < 2) return false;
  if (parseImplicitSession(hash)) return true;
  const params = new URLSearchParams(hash.replace(/^#/, ''));
  return params.has('error') || params.has('error_code') || params.has('error_description');
}

/** @returns {string|null} le fragment retiré de l'URL */
export function stashImplicitAuthHash() {
  if (typeof window === 'undefined' || !window.location) return null;
  const { hash, pathname, search } = window.location;
  if (!isSupabaseCallbackHash(hash)) return null;
  window.history.replaceState(window.history.state, '', pathname + search);
  stashedHash = hash;
  return hash;
}

/**
 * Le fragment de callback courant : celui de l'URL s'il y en a un (page
 * ouverte sans passer par le stash, ex. tests), sinon celui mis de côté.
 * @returns {string} '' quand il n'y a rien
 */
export function readAuthHash() {
  const urlHash = typeof window !== 'undefined' && window.location ? window.location.hash : '';
  if (isSupabaseCallbackHash(urlHash)) return urlHash;
  return stashedHash || '';
}

/** Oublie le fragment mis de côté (une fois la session installée, ou en test). */
export function clearStashedAuthHash() {
  stashedHash = null;
}
